import React, { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from "@mui/material";
import { useBulkUpdateMediaLateralitiesMutation } from "./dataAccess";
import DisplayText from "../../components/DisplayText/DisplayText";
import { Box } from "@mui/system";
import { useSnackbar } from "notistack";

interface IEditLateralityProps {
    isOpen: boolean;
    onClose: () => void;
    currentLaterality?: string;
    patientId: string;
    mediaIds: string[];
    selectStudies?: (mediaIds: string[]) => void;
}
const EditLaterality: React.FC<IEditLateralityProps> = ({
    isOpen,
    onClose,
    currentLaterality,
    patientId,
    mediaIds,
    selectStudies = () => {},
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [bulkUpdateLateralities] = useBulkUpdateMediaLateralitiesMutation();
    const [selectedLaterality, setSelectedLaterality] = useState("");
    const lateralities = ["OS", "OD", "OU"];

    useEffect(() => {
        if (!currentLaterality) return;
        setSelectedLaterality(currentLaterality);
    }, [currentLaterality]);

    const handleSaveLateralityChange = async () => {
        const currentSelectedMediaIds = mediaIds;
        selectStudies([]);
        onClose();
        try {
            const mediaLateralitiesUpdates = mediaIds.map((mediaId) => ({
                uuid: mediaId,
                laterality: selectedLaterality,
            }));
            const res = await bulkUpdateLateralities({ patientId, mediaLateralitiesUpdates }).unwrap();

            if (
                !mediaLateralitiesUpdates.every((media) => res.find((updatedMedia) => updatedMedia.uuid === media.uuid))
            ) {
                const unUpdates = mediaLateralitiesUpdates.filter(
                    (media) => !res.find((updatedMedia) => updatedMedia.uuid === media.uuid)
                );
                enqueueSnackbar(
                    `someting goes wrong updating medias with ids[${unUpdates.map((media) => media.uuid).join(", ")}]`,
                    { variant: "error" }
                );
            }
        } catch (e) {
            selectStudies(currentSelectedMediaIds);
            enqueueSnackbar("Error updating laterality", { variant: "error" });
        }
    };

    const handleChangeLaterality = (event: SelectChangeEvent) => {
        const newLaterality = event.target.value as string;
        setSelectedLaterality(newLaterality);
    };

    if (!isOpen) return null;

    const title = mediaIds.length > 1 ? "Edit lateralities" : "Edit laterality";

    return (
        <Dialog maxWidth="sm" fullWidth open={true} onClose={onClose}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 24, pb: 0 }}>
                <DisplayText text={title} type="h5" />
            </Box>
            <DialogContent>
                <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">Select laterality</InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        value={selectedLaterality}
                        label="Select Laterality"
                        onChange={handleChangeLaterality}
                    >
                        {lateralities.map((laterality: string) => (
                            <MenuItem key={laterality} value={laterality}>
                                {laterality}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="contained"
                    disabled={Boolean(!selectedLaterality || selectedLaterality === currentLaterality)}
                    onClick={handleSaveLateralityChange}
                >
                    Accept
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default React.memo(EditLaterality);
